import React from "react";
import Card from "@mui/material/Card";
import CardContent from "@mui/material/CardContent";
import CardMedia from "@mui/material/CardMedia";
import { CardActionArea, Chip, LinearProgress } from "@mui/material";
import Header from "components/Header";
import { Box, Button, Typography, Grid, TextField } from "@mui/material";
import { useNavigate } from "react-router-dom";
import { getAllDashboardData, getAllOwners } from "state/apis";
import { setPropertyAsDefault, getDefaultProperty } from "state/index";
import { toast } from "react-toastify";
import { useDispatch, useSelector } from "react-redux";
import Autocomplete from "@mui/material/Autocomplete";
import ApartmentIcon from "@mui/icons-material/Apartment";
import MeetingRoomIcon from "@mui/icons-material/MeetingRoom";
import DoorFrontIcon from "@mui/icons-material/DoorFront";
import PersonIcon from "@mui/icons-material/Person";
import StatBox from "components/StatBox";
import { designTokens } from "theme";

const Dashboard = () => {
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const [isLoading, setIsLoading] = React.useState(true);
    const [data, setData] = React.useState([]);
    const [propertyId, setPropertyId] = React.useState("");
    const token = localStorage.getItem("token");
    const [owners, setOwners] = React.useState([]);
    const [filter, setFilter] = React.useState({ ownerId: "" });
    const property = useSelector((state) => state.global.propertyName);

    React.useEffect(() => { fetchData(); fetchOwners(); }, [filter]);
    React.useEffect(() => { if (propertyId !== "") setProperty(); }, [propertyId]);

    const fetchData = async () => {
        try {
            setIsLoading(true);
            const res = await getAllDashboardData(filter.ownerId);
            setData(res?.data);
            setIsLoading(false);
        } catch (error) {
            console.error("Error fetching data:", error);
            toast.error("Failed to fetch data. Please try again.");
        }
    };

    const fetchOwners = async () => {
        try {
            const res = await getAllOwners(100000, 0);
            setOwners(res?.owners);
        } catch (error) { console.error("Error fetching owners:", error); }
    };

    if (!token) navigate("/login", { replace: true });

    const setProperty = async () => {
        try {
            setIsLoading(true);
            await setPropertyAsDefault(dispatch, propertyId);
            await getDefaultProperty(dispatch);
            setIsLoading(false);
        } catch (error) { console.error("Error fetching data:", error); }
    };

    const handleOwnerChange = (newValue) => {
        setFilter((prev) => ({ ...prev, ownerId: newValue ? newValue.id : null }));
    };

    if (isLoading) {
        return (
            <Box sx={{ p: "24px 28px" }}>
                <Header title="Dashboard" />
                <Box sx={{ mt: 4, textAlign: "center", color: "#5a6a85" }}>Loading...</Box>
            </Box>
        );
    }

    const properties = data?.properties?.filter((p) => !p.isSold) || [];
    const totalProperties = properties.length;
    const totalUnits = properties.reduce((sum, p) => sum + (p.totalUnits || 0), 0);
    const totalVacant = properties.reduce((sum, p) => sum + (p.totalVacant || 0), 0);
    const totalOccupied = totalUnits - totalVacant;
    const occupancyRate = totalUnits > 0 ? Math.round((totalOccupied / totalUnits) * 100) : 0;

    return (
        <Box sx={{ p: "24px 28px" }}>
            <Header title="Dashboard" />

            <Box display="flex" gap="16px" mt="20px" mb="20px">
                <StatBox title="Total Properties" value={totalProperties} increase=""
                    description=""
                    icon={<ApartmentIcon sx={{ color: "#0b5cab", fontSize: "22px" }} />}
                />
                <StatBox title="Total Units" value={totalUnits} increase={`${occupancyRate}% Occupied`}
                    description={<LinearProgress variant="determinate" value={occupancyRate} sx={{ width: 60, "& .MuiLinearProgress-bar": { backgroundColor: "#0b5cab" } }} />}
                    icon={<MeetingRoomIcon sx={{ color: "#2e7d32", fontSize: "22px" }} />}
                />
                <StatBox title="Occupied Units" value={totalOccupied}
                    increase={`${totalUnits > 0 ? Math.round((totalOccupied / totalUnits) * 100) : 0}%`}
                    description="Active contracts"
                    icon={<PersonIcon sx={{ color: "#ed6c02", fontSize: "22px" }} />}
                />
                <StatBox title="Vacant Units" value={totalVacant}
                    increase={`${totalUnits > 0 ? Math.round((totalVacant / totalUnits) * 100) : 0}%`}
                    description="Available for rent"
                    icon={<DoorFrontIcon sx={{ color: "#d32f2f", fontSize: "22px" }} />}
                />
            </Box>

            <Box sx={{ display: "flex", justifyContent: "flex-end", p: 2, backgroundColor: "#fff", borderRadius: "8px", border: "1px solid #dde2ea", mb: 3 }}>
                <Grid container spacing={1} alignItems="center">
                    <Grid item xs={3}>
                        <Autocomplete disablePortal options={owners} getOptionLabel={(option) => option.name}
                            value={filter.ownerId ? owners.find((o) => o.id === filter.ownerId) : null}
                            onChange={(e, newValue) => handleOwnerChange(newValue)}
                            isOptionEqualToValue={(option, value) => option === value}
                            renderOption={(props, option) => <li {...props} key={option.id} style={{ fontSize: "0.8125rem" }}>{option.name}</li>}
                            renderInput={(params) => <TextField {...params} fullWidth label="Filter by Owner" size="small" />}
                        />
                    </Grid>
                </Grid>
            </Box>

            <Grid container spacing={2.5}>
                {properties.length === 0 ? (
                    <Grid item xs={12}>
                        <Box sx={{ textAlign: "center", py: 6, backgroundColor: "#fff", borderRadius: "8px", border: "1px solid #dde2ea" }}>
                            <Typography variant="body1" sx={{ color: "#5a6a85" }}>No properties found</Typography>
                        </Box>
                    </Grid>
                ) : (
                    properties.map((val, index) => {
                        const occupied = (val.totalUnits || 0) - (val.totalVacant || 0);
                        const occRate = val.totalUnits > 0 ? Math.round((occupied / val.totalUnits) * 100) : 0;
                        const isDefault = val.propertyName === property;
                        return (
                            <Grid item xs={12} sm={6} md={3} lg={3} key={index}>
                                <Card sx={{
                                    maxWidth: 300, backgroundColor: "#fff", borderRadius: "8px",
                                    border: isDefault ? `2px solid ${designTokens.primary.main}` : "1px solid #dde2ea",
                                    boxShadow: "0 1px 3px rgba(0,0,0,0.06)",
                                    "&:hover": { boxShadow: "0 4px 12px rgba(0,0,0,0.1)" },
                                }}>
                                    <CardActionArea>
                                        <Box sx={{ position: "relative" }}>
                                            <CardMedia component="img" height="120" image={val?.image} alt={val?.propertyName} sx={{ objectFit: "cover" }} />
                                            <Box sx={{ position: "absolute", top: 8, right: 8 }}>
                                                {isDefault ? (
                                                    <Chip label="Default" size="small" sx={{ backgroundColor: "#d6e8f7", color: "#0b5cab", fontWeight: 600, fontSize: "0.6875rem" }} />
                                                ) : null}
                                            </Box>
                                        </Box>
                                        <CardContent sx={{ pb: 1.5, px: 2 }}>
                                            <Typography sx={{ fontWeight: 600, fontSize: "0.875rem", color: "#1a2138" }} noWrap>{val?.propertyName}</Typography>
                                            <Typography variant="caption" sx={{ color: "#5a6a85", display: "block", mb: 1.5 }}>{val?.owner?.name}</Typography>

                                            <Box display="flex" justifyContent="space-between" sx={{ backgroundColor: "#f4f6f9", borderRadius: "6px", p: 1, mb: 1.5 }}>
                                                <Box textAlign="center">
                                                    <Typography sx={{ fontWeight: 700, fontSize: "1rem", color: "#1a2138" }}>{val?.totalUnits}</Typography>
                                                    <Typography sx={{ fontSize: "0.6rem", color: "#5a6a85", textTransform: "uppercase", fontWeight: 600 }}>Units</Typography>
                                                </Box>
                                                <Box textAlign="center">
                                                    <Typography sx={{ fontWeight: 700, fontSize: "1rem", color: "#2e7d32" }}>{occupied}</Typography>
                                                    <Typography sx={{ fontSize: "0.6rem", color: "#5a6a85", textTransform: "uppercase", fontWeight: 600 }}>Occupied</Typography>
                                                </Box>
                                                <Box textAlign="center">
                                                    <Typography sx={{ fontWeight: 700, fontSize: "1rem", color: "#d32f2f" }}>{val?.totalVacant}</Typography>
                                                    <Typography sx={{ fontSize: "0.6rem", color: "#5a6a85", textTransform: "uppercase", fontWeight: 600 }}>Vacant</Typography>
                                                </Box>
                                            </Box>

                                            <Box>
                                                <Box display="flex" justifyContent="space-between" mb={0.5}>
                                                    <Typography sx={{ fontSize: "0.6rem", color: "#5a6a85", textTransform: "uppercase", fontWeight: 600 }}>Occupancy</Typography>
                                                    <Typography sx={{ fontWeight: 700, fontSize: "0.75rem", color: "#1a2138" }}>{occRate}%</Typography>
                                                </Box>
                                                <LinearProgress variant="determinate" value={occRate} sx={{
                                                    height: 5, borderRadius: 4, backgroundColor: "#dde2ea",
                                                    "& .MuiLinearProgress-bar": { borderRadius: 4, backgroundColor: occRate > 80 ? "#2e7d32" : occRate > 50 ? "#ed6c02" : "#d32f2f" },
                                                }} />
                                            </Box>

                                            <Box sx={{ display: "flex", justifyContent: "center", mt: 1.5 }}>
                                                <Button variant={isDefault ? "outlined" : "contained"} size="small" onClick={() => setPropertyId(val.id)} disabled={isDefault} fullWidth
                                                    sx={{
                                                        borderRadius: "6px", py: 0.6, fontSize: "0.75rem", fontWeight: 600,
                                                        ...(isDefault ? { borderColor: "#c1c9d6", color: "#5a6a85" } : {}),
                                                    }}
                                                >
                                                    {isDefault ? "Selected" : "Set As Default"}
                                                </Button>
                                            </Box>
                                        </CardContent>
                                    </CardActionArea>
                                </Card>
                            </Grid>
                        );
                    })
                )}
            </Grid>
        </Box>
    );
};

export default Dashboard;
